import type { ChangeEvent, ReactNode } from 'react';

import { ErrorApi } from '../api/cliente';

/** Cabecera de pantalla: título, una línea de contexto y las acciones. */
export function Encabezado({
  titulo,
  nota,
  children,
}: {
  titulo: string;
  nota?: string;
  children?: ReactNode;
}) {
  return (
    <header className="encabezado">
      <div>
        <h1>{titulo}</h1>
        {nota && <p>{nota}</p>}
      </div>
      {children && <div className="fila">{children}</div>}
    </header>
  );
}

export function Tarjeta({ titulo, children }: { titulo?: string; children: ReactNode }) {
  return (
    <section className="tarjeta">
      {titulo && (
        <div className="tarjeta__titulo">
          <h2>{titulo}</h2>
        </div>
      )}
      {children}
    </section>
  );
}

/**
 * Muestra un error de la API.
 *
 * Incluye el `traceId` cuando lo hay: es lo que conecta lo que ve el asesor con
 * la traza del servidor, y evita el «me salió un error» sin más.
 */
/**
 * Nombre legible de los campos que la API señala en sus validaciones.
 *
 * <p>La API responde con el nombre técnico del campo —`direccionPublica`,
 * `latitud`— porque el que lo consume puede ser el panel o una integración.
 * Aquí se traduce a lo que dice la etiqueta del formulario, que es lo que quien
 * lo lee tiene delante en la pantalla. Un campo sin traducir se muestra tal
 * cual: es mejor un nombre técnico que ningún nombre.
 */
const NOMBRES: Record<string, string> = {
  descripcion: 'Descripción',
  direccionPublica: 'Dirección pública',
  direccion: 'Dirección',
  latitud: 'Ubicación en el mapa',
  longitud: 'Ubicación en el mapa',
  operaciones: 'Operaciones (venta o arriendo)',
  areaConstruida: 'Área construida',
  areaLote: 'Área del lote',
  habitaciones: 'Habitaciones',
  banos: 'Baños',
  fotos: 'Fotos',
  tipoInmuebleId: 'Tipo de inmueble',
  sedeId: 'Oficina',
  ciudadId: 'Ciudad',
  titulo: 'Título',
  caracteristicas: 'Características',
  recorridoUrl: 'Recorrido 3D',
};

/**
 * Un fallo, con el detalle que la API mandó.
 *
 * <p>La lista de campos se pinta. Durante un tiempo se descartaba y solo se
 * mostraba la frase general con el identificador de traza: al intentar
 * publicar un inmueble incompleto se leía «el inmueble no está completo para
 * salir al portal» sin decir qué faltaba, cuando la API venía enviando la
 * lista exacta. Quien está delante del formulario necesita saber qué arreglar,
 * no que algo está mal.
 */
export function Aviso({ error, exito }: { error?: unknown; exito?: string | null }) {
  if (exito) return <p className="aviso aviso--bien">{exito}</p>;
  if (!error) return null;

  const mensaje = error instanceof Error ? error.message : 'Algo salió mal.';
  const traza = error instanceof ErrorApi ? error.traceId : undefined;
  const campos = error instanceof ErrorApi ? error.campos : [];

  return (
    <div className="aviso aviso--mal">
      <p style={{ margin: 0 }}>
        {mensaje}
        {traza && (
          <>
            {' · '}
            <code>{traza}</code>
          </>
        )}
      </p>
      {campos.length > 0 && (
        <ul style={{ margin: '8px 0 0', paddingLeft: 20 }}>
          {campos.map((e) => (
            <li key={`${e.campo}-${e.mensaje}`}>
              <strong>{NOMBRES[e.campo] ?? e.campo}</strong>: {e.mensaje}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

interface CampoBase {
  etiqueta: string;
  ayuda?: string;
  error?: string;
  requerido?: boolean;
}

export function Campo({
  etiqueta,
  ayuda,
  error,
  requerido,
  tipo = 'text',
  valor,
  alCambiar,
  ...resto
}: CampoBase & {
  tipo?: string;
  valor: string | number | null | undefined;
  alCambiar: (valor: string) => void;
  min?: number;
  max?: number;
  step?: string;
  placeholder?: string;
  disabled?: boolean;
}) {
  return (
    <div className={error ? 'campo campo--mal' : 'campo'}>
      <label>
        {etiqueta}
        {requerido && ' *'}
      </label>
      <input
        type={tipo}
        value={valor ?? ''}
        onChange={(e: ChangeEvent<HTMLInputElement>) => alCambiar(e.target.value)}
        {...resto}
      />
      {error ? <p className="campo__error">{error}</p> : ayuda && <p className="campo__ayuda">{ayuda}</p>}
    </div>
  );
}

export function AreaTexto({
  etiqueta,
  ayuda,
  error,
  requerido,
  valor,
  alCambiar,
  filas = 4,
}: CampoBase & { valor: string | null | undefined; alCambiar: (valor: string) => void; filas?: number }) {
  return (
    <div className={error ? 'campo campo--mal' : 'campo'}>
      <label>
        {etiqueta}
        {requerido && ' *'}
      </label>
      <textarea rows={filas} value={valor ?? ''} onChange={(e) => alCambiar(e.target.value)} />
      {error ? <p className="campo__error">{error}</p> : ayuda && <p className="campo__ayuda">{ayuda}</p>}
    </div>
  );
}

export function Seleccion({
  etiqueta,
  ayuda,
  error,
  requerido,
  valor,
  alCambiar,
  opciones,
  vacio,
  disabled,
}: CampoBase & {
  valor: string | null | undefined;
  alCambiar: (valor: string) => void;
  opciones: { valor: string; texto: string }[];
  vacio?: string;
  disabled?: boolean;
}) {
  return (
    <div className={error ? 'campo campo--mal' : 'campo'}>
      <label>
        {etiqueta}
        {requerido && ' *'}
      </label>
      <select value={valor ?? ''} onChange={(e) => alCambiar(e.target.value)} disabled={disabled}>
        {vacio !== undefined && <option value="">{vacio}</option>}
        {opciones.map((o) => (
          <option key={o.valor} value={o.valor}>
            {o.texto}
          </option>
        ))}
      </select>
      {error ? <p className="campo__error">{error}</p> : ayuda && <p className="campo__ayuda">{ayuda}</p>}
    </div>
  );
}

export function Casilla({
  etiqueta,
  ayuda,
  valor,
  alCambiar,
}: {
  etiqueta: string;
  ayuda?: string;
  valor: boolean;
  alCambiar: (valor: boolean) => void;
}) {
  return (
    <div className="campo">
      <div className="campo__casilla">
        <input type="checkbox" checked={valor} onChange={(e) => alCambiar(e.target.checked)} />
        <label>{etiqueta}</label>
      </div>
      {ayuda && <p className="campo__ayuda">{ayuda}</p>}
    </div>
  );
}

/** Los tonos siguen los del estado comercial de la marca. */
const TONOS: Record<string, string> = {
  PUBLICADO: 'verde',
  DISPONIBLE: 'verde',
  CONFIRMADA: 'verde',
  GANADO: 'verde',
  ACTIVO: 'verde',
  BORRADOR: '',
  NUEVO: 'azul',
  EN_REVISION: 'ambar',
  PENDIENTE: 'ambar',
  RESERVADO: 'ambar',
  PAUSADO: 'ambar',
  CONTACTADO: 'azul',
  CALIFICADO: 'azul',
  VISITA_AGENDADA: 'azul',
  NEGOCIACION: 'azul',
  REPROGRAMADA: 'ambar',
  COMPLETADA: 'verde',
  ARCHIVADO: '',
  CANCELADA: 'roja',
  NO_ASISTIO: 'roja',
  PERDIDO: 'roja',
  DESCARTADO: 'roja',
  SUSPENDIDO: 'roja',
  VENDIDO: '',
  ARRENDADO: '',
  RETIRADO: '',
};

export function Etiqueta({ children }: { children: string }) {
  const tono = TONOS[children];
  return (
    <span className={tono ? `etiqueta etiqueta--${tono}` : 'etiqueta'}>
      {children.replaceAll('_', ' ').toLowerCase()}
    </span>
  );
}

export function Vacio({ children }: { children: ReactNode }) {
  return <p className="vacio">{children}</p>;
}

export function Cargando() {
  return <p className="vacio">Cargando…</p>;
}

/** Pesos colombianos sin decimales: nadie negocia un apartamento por centavos. */
export function pesos(valor: number | null | undefined): string {
  if (valor == null) return '—';
  return new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    maximumFractionDigits: 0,
  }).format(valor);
}

export function fecha(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('es-CO', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

export function fechaHora(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('es-CO', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function Paginador({
  pagina,
  total,
  paginas,
  alCambiar,
}: {
  pagina: number;
  total: number;
  paginas: number;
  alCambiar: (pagina: number) => void;
}) {
  if (paginas <= 1) return <p className="paginacion">{total} en total</p>;
  return (
    <div className="paginacion">
      <span>
        {total} en total · página {pagina + 1} de {paginas}
      </span>
      <span className="fila">
        <button className="secundario" disabled={pagina === 0} onClick={() => alCambiar(pagina - 1)}>
          Anterior
        </button>
        <button
          className="secundario"
          disabled={pagina + 1 >= paginas}
          onClick={() => alCambiar(pagina + 1)}
        >
          Siguiente
        </button>
      </span>
    </div>
  );
}
